import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import axiosEmailInstance from "@/Backend/IghotaVirtualLearning/axiosEmailInstance";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";

export default function ContactPage() {
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		inquiry: "",
		subject: "",
		message: "",
	});

	const [status, setStatus] = useState({
		submitting: false,
		submitted: false,
		info: { error: false, msg: null },
	});

	const validateForm = () => {
		const { email, subject, message, firstName, lastName } = formData;
		return email && subject && message && firstName && lastName;
	};

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setStatus((prevStatus) => ({ ...prevStatus, submitting: true }));

		if (validateForm()) {
			try {
				await axiosEmailInstance.post("/send-email", {
					subject: `New Contact Form Message from ${formData.firstName} ${formData.lastName}`,
					text: `
						Name: ${formData.firstName} ${formData.lastName}
						Email: ${formData.email}
						Inquiry: ${formData.inquiry}
						Subject: ${formData.subject}
						Message: ${formData.message}
					`,
				});

				setStatus({
					submitted: true,
					submitting: false,
					info: { error: false, msg: "Message sent successfully!" },
				});

				setFormData({
					firstName: "",
					lastName: "",
					email: "",
					inquiry: "",
					subject: "",
					message: "",
				});

				toast.success(
					"Thank you for your message. We will get back to you soon!",
					{
						position: "top-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						style: {
							background: "#FAB900",
							color: "#000000",
						},
					}
				);
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						formData,
						error: error.message,
					},
				});

				setStatus({
					submitted: false,
					submitting: false,
					info: { error: true, msg: "An error has occurred." },
				});

				console.error("Error submitting form:", error);

				toast.error(
					"There was an error sending your message. Please try again.",
					{
						position: "top-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						style: {
							background: "#fff",
							color: "#000000",
						},
					}
				);
			}
		} else {
			setStatus((prevStatus) => ({ ...prevStatus, submitting: false }));
			toast.error("Kindly please fill all the fields, and try again.", {
				position: "top-center",
				autoClose: 5000,
				hideProgressBar: true,
				closeOnClick: true,
				pauseOnHover: true,
				style: {
					background: "#fff",
					color: "#000000",
				},
			});
		}
	};

	return (
		<main className="flex-1">
			<ToastContainer />
			<section className="w-full py-12 md:py-24 lg:py-32">
				<div className="container px-4 md:px-6">
					<div className="flex flex-col items-center justify-center space-y-4 text-center">
						<div className="space-y-2">
							<h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl">
								Contact Us
							</h1>
							<p className="mx-auto max-w-[700px] text-muted-foreground md:text-xl">
								Have questions or feedback? We'd love to hear
								from you. Get in touch with our team.
							</p>
						</div>
					</div>
					<div className="mx-auto grid max-w-5xl gap-8 py-12 md:grid-cols-2">
						<div className="space-y-6">
							<Card>
								<CardHeader>
									<CardTitle>Contact Information</CardTitle>
									<CardDescription>
										Reach out to us through any of these
										channels.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="flex items-start gap-4">
										<MapPin className="h-5 w-5 text-primary mt-0.5" />
										<div>
											<h3 className="font-medium">
												Address
											</h3>
											<p className="text-sm text-muted-foreground">
												123 Education Street, Lagos,
												Nigeria
											</p>
										</div>
									</div>
									<div className="flex items-start gap-4">
										<Phone className="h-5 w-5 text-primary mt-0.5" />
										<div>
											<h3 className="font-medium">
												Phone
											</h3>
											<p className="text-sm text-muted-foreground">
												+234 123 456 7890
											</p>
										</div>
									</div>
									<div className="flex items-start gap-4">
										<Mail className="h-5 w-5 text-primary mt-0.5" />
										<div>
											<h3 className="font-medium">
												Email
											</h3>
											<p className="text-sm text-muted-foreground">
												info@ighota.org
											</p>
										</div>
									</div>
								</CardContent>
							</Card>
							<Card>
								<CardHeader>
									<CardTitle>Office Hours</CardTitle>
									<CardDescription>
										When you can visit or call us.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-2">
									<div className="flex justify-between">
										<span>Monday - Friday</span>
										<span>9:00 AM - 5:00 PM</span>
									</div>
									<div className="flex justify-between">
										<span>Saturday</span>
										<span>10:00 AM - 2:00 PM</span>
									</div>
									<div className="flex justify-between">
										<span>Sunday</span>
										<span>Closed</span>
									</div>
								</CardContent>
							</Card>
						</div>
						<Card>
							<CardHeader>
								<CardTitle>Send us a Message</CardTitle>
								<CardDescription>
									Fill out the form below and we'll get back
									to you as soon as possible.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<form
									className="space-y-4"
									onSubmit={handleSubmit}>
									<div className="grid gap-2">
										<Label htmlFor="firstName">
											First Name
										</Label>
										<Input
											id="firstName"
											name="firstName"
											placeholder="Your first name"
											value={formData.firstName}
											onChange={handleChange}
										/>
									</div>
									<div className="grid gap-2">
										<Label htmlFor="lastName">
											Last Name
										</Label>
										<Input
											id="lastName"
											name="lastName"
											placeholder="Your last name"
											value={formData.lastName}
											onChange={handleChange}
										/>
									</div>
									<div className="grid gap-2">
										<Label htmlFor="email">Email</Label>
										<Input
											id="email"
											name="email"
											type="email"
											placeholder="Your email"
											value={formData.email}
											onChange={handleChange}
										/>
									</div>
									<div className="grid gap-2">
										<Label htmlFor="inquiry">
											Inquiry Type
										</Label>
										<Input
											id="inquiry"
											name="inquiry"
											placeholder="e.g. General, Technical, Partnership"
											value={formData.inquiry}
											onChange={handleChange}
										/>
									</div>
									<div className="grid gap-2">
										<Label htmlFor="subject">Subject</Label>
										<Input
											id="subject"
											name="subject"
											placeholder="Subject of your message"
											value={formData.subject}
											onChange={handleChange}
										/>
									</div>
									<div className="grid gap-2">
										<Label htmlFor="message">Message</Label>
										<Textarea
											id="message"
											name="message"
											placeholder="Your message"
											className="min-h-[150px]"
											value={formData.message}
											onChange={handleChange}
										/>
									</div>
									<Button
										type="submit"
										className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
										disabled={status.submitting}>
										{status.submitting
											? "Sending..."
											: "Send Message"}
									</Button>
								</form>
							</CardContent>
						</Card>
					</div>
				</div>
			</section>
		</main>
	);
}
